import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import * as s from "../InputPage.styles";
import { AnalysisForm, AreaUnit } from "../machine";
import { useFormattedNumberField } from "./useFormattedNumberField";
import { convertPriceUnit, formatManToEokMan, formatNumber, formatPriceNumber, toM2 } from "../function";

const unitList: (AreaUnit | "총 비용")[] = ["평", "m²", "ha", "10a", "총 비용"];
interface Props {
  formData: AnalysisForm;
  setFormData: Dispatch<SetStateAction<AnalysisForm>>;
}

// 10a 기준
const defaultValue = {
  // 금융비
  financeCost: 235842,
  // 운송비
  transportationCost: 10000,
  // 기타
  others: 90000,
}

function CostSection({ formData, setFormData }: Props) {
  // 단위 교환 버튼
  const [openUnitChange, setOpenUnitChange] = useState(false);
  const [unitPriceInputMode, setUnitPriceInputMode] = useState<"total" | "unitPrice">("unitPrice")
  const [unit, setUnit] = useState<any>(formData.unit);

  const totalRef = useRef<HTMLDivElement>(null);

  const handleChangeUnit = (v: string) => {
    if (v === "연") {
      setFormData((prev) => ({
        ...prev,
        isCostYear: true,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        isCostYear: false,
      }));
    }
  };

  const unitRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // 열려있는 드롭다운에 따라 참조할 ref 선택
      const currentRef = unitRef.current;

      if (currentRef && !currentRef.contains(target)) {
        setOpenUnitChange(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const [openDetailCost, setOpenDetailCost] = useState(false);
  const [openSgaCost, setOpenSgaCost] = useState(false);

  const updateCostByRate = (
    field:
      | "financialCost"
      | "empLaborCost"
      | "ownLaborCost"
      | "landCost"
      | "materialCost"
      | "waterHeatingCost"
      | "farmEquipmentRentalCost"
      | "sga",
    rate: number
  ) => {
    setFormData((prev) => {
      const current = prev[field] || 0;
      const next = Math.max(Math.round(current * rate), 0);
      return {
        ...prev,
        [field]: next,
      };
    });
  };

  const scaleAllCosts = (rate: number) => {
    setFormData((prev) => {
      const next: AnalysisForm = { ...prev };

      next.financialCost = Math.max(Math.round(prev.financialCost * rate), 0);
      next.empLaborCost = Math.max(Math.round(prev.empLaborCost * rate), 0);
      next.ownLaborCost = Math.max(Math.round(prev.ownLaborCost * rate), 0);
      next.landCost = Math.max(Math.round(prev.landCost * rate), 0);
      next.materialCost = Math.max(Math.round(prev.materialCost * rate), 0);
      next.waterHeatingCost = Math.max(
        Math.round(prev.waterHeatingCost * rate),
        0
      );
      next.farmEquipmentRentalCost = Math.max(
        Math.round(prev.farmEquipmentRentalCost * rate),
        0
      );
      next.sga = Math.max(Math.round(prev.sga * rate), 0);

      return next;
    });
  };

  const allArea = toM2(formData.allArea, "평");
  const area = formData.area ? toM2(formData.area, "평") : toM2(formData.allArea, "평");

  const {
    display: empLaborDisplay,
    handleChange: handleEmpLaborChange,
    handleBlur: handleEmpLaborBlur,
  } = useFormattedNumberField({
    baseValue: unit === "총 비용" ? formData.empLaborCost : formData.empLaborCost / (area / toM2(1, unit)),
    onChangeBaseValue: (v) =>
      setFormData((prev) => ({ ...prev, empLaborCost: unit === "총 비용" ? v : v * (area / toM2(1, unit)) })),
  });

  const {
    display: ownLaborDisplay,
    handleChange: handleOwnLaborChange,
    handleBlur: handleOwnLaborBlur,
  } = useFormattedNumberField({
    baseValue: unit === "총 비용" ? formData.ownLaborCost : formData.ownLaborCost / (allArea / toM2(1, unit)),
    onChangeBaseValue: (v) =>
      setFormData((prev) => ({ ...prev, ownLaborCost: unit === "총 비용" ? v : v * (allArea / toM2(1, unit)) })),
  });

  const {
    display: materialDisplay,
    handleChange: handleMaterialChange,
    handleBlur: handleMaterialBlur,
  } = useFormattedNumberField({
    baseValue: unit === "총 비용" ? formData.materialCost : formData.materialCost / (allArea / toM2(1, unit)),
    onChangeBaseValue: (v) =>
      setFormData((prev) => ({ ...prev, materialCost: unit === "총 비용" ? v : v * (allArea / toM2(1, unit)) })),
  });

  const {
    display: waterHeatingDisplay,
    handleChange: handleWaterHeatingChange,
    handleBlur: handleWaterHeatingBlur,
  } = useFormattedNumberField({
    baseValue: unit === "총 비용" ? formData.waterHeatingCost : formData.waterHeatingCost / (allArea / toM2(1, unit)),
    onChangeBaseValue: (v) =>
      setFormData((prev) => ({ ...prev, waterHeatingCost: unit === "총 비용" ? v : v * (allArea / toM2(1, unit)) })),
  });

  const {
    display: rentalDisplay,
    handleChange: handleRentalChange,
    handleBlur: handleRentalBlur,
  } = useFormattedNumberField({
    baseValue: unit === "총 비용" ? formData.farmEquipmentRentalCost : formData.farmEquipmentRentalCost / (allArea / toM2(1, unit)),
    onChangeBaseValue: (v) =>
      setFormData((prev) => ({ ...prev, farmEquipmentRentalCost: unit === "총 비용" ? v : v * (allArea / toM2(1, unit)) })),
  });

  // 판관비 = 농기계 수리비 + 농지임대료 + 금융비 + 운송비 + 식비 + 사무용품 및 잡비 + 기타
  const [totalSga, setTotalSga] = useState(0);
  const [repairCost, setRepairCost] = useState(0);
  const [landRentalCost, setLandRentalCost] = useState(0);
  const [financeCost, setFinanceCost] = useState(0);
  const [transportationCost, setTransportationCost] = useState(0);
  const [mealCost, setMealCost] = useState(0);
  const [officeSuppliesCost, setOfficeSuppliesCost] = useState(0);
  const [othersCost, setOthersCost] = useState(0);

  useEffect(() => {
    setFinanceCost(Math.ceil(convertPriceUnit(defaultValue.financeCost, "10a", formData.unit) 
    // / (allArea / toM2(1, formData.unit))
    ))
    setTransportationCost(Math.ceil(convertPriceUnit(defaultValue.transportationCost, "10a", formData.unit) 
    // / (allArea / toM2(1, formData.unit))
    ))
    setOthersCost(Math.ceil(convertPriceUnit(defaultValue.others, "10a", formData.unit) 
    // / (area / toM2(1, formData.unit))
    ))
  }, [allArea, area, formData.unit]);

  useEffect(() => {
    if (unit === "총 비용") {
      setFinanceCost(Math.ceil(convertPriceUnit(defaultValue.financeCost, "10a", unit) * (allArea / toM2(1, unit))))
      setTransportationCost(Math.ceil(convertPriceUnit(defaultValue.transportationCost, "10a", unit) * (allArea / toM2(1, unit))))
      setOthersCost(Math.ceil(convertPriceUnit(defaultValue.others, "10a", unit) * (area / toM2(1, unit))))
    } else {
      setFinanceCost(Math.ceil(convertPriceUnit(defaultValue.financeCost, "10a", unit)))
      setTransportationCost(Math.ceil(convertPriceUnit(defaultValue.transportationCost, "10a", unit)))
      setOthersCost(Math.ceil(convertPriceUnit(defaultValue.others, "10a", unit)))
    }
  }, [unit])

  useEffect(() => {
    if (unit === "총 비용") {
      const totalSga = repairCost + landRentalCost + financeCost + transportationCost + mealCost + officeSuppliesCost + othersCost;

      setTotalSga(totalSga);
      setFormData((prev) => ({
        ...prev,
        sga: totalSga,
      }));
    } else {
      const totalUnitPrice = repairCost + landRentalCost + financeCost + transportationCost + mealCost + officeSuppliesCost + othersCost;
      const totalCost = Math.ceil(
        repairCost * (allArea / toM2(1, formData.unit)) +
        landRentalCost * (allArea / toM2(1, formData.unit)) +
        financeCost * (allArea / toM2(1, formData.unit)) +
        transportationCost * (allArea / toM2(1, formData.unit)) +
        mealCost * (allArea / toM2(1, formData.unit)) +
        officeSuppliesCost * (allArea / toM2(1, formData.unit)) +
        othersCost * (area / toM2(1, formData.unit))
      );
      setTotalSga(totalUnitPrice);
      setFormData((prev) => ({
        ...prev,
        sga: totalCost,
      }));
    }
  }, [repairCost, landRentalCost, financeCost, transportationCost, mealCost, officeSuppliesCost, othersCost]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      sga: unit === "총 비용" ? totalSga : prev.sga,
    }));
  }, [totalSga])

  return (
    <s.Section>
      <s.SectionTitleContainer>
        <s.SectionTitle>4. 비용</s.SectionTitle>
        <s.UnitWrapper ref={unitRef}>
          <s.UnitChangeButton onClick={() => setOpenUnitChange(true)}>
            <span>단위</span>
            {unit}
          </s.UnitChangeButton>
          {openUnitChange && (
            <s.UnitChangeDropdown>
              {unitList.map((i) => (
                <s.UnitChangeDropdownItem
                  key={i}
                  onClick={() => setUnit(i as any)}
                  $active={unit === i}
                >
                  {i}
                  {unit === i && (
                    <img src="/asset/images/check/Check24Green2.svg" alt="" />
                  )}
                </s.UnitChangeDropdownItem>
              ))}
            </s.UnitChangeDropdown>
          )}
        </s.UnitWrapper>
        {/* <s.UnitWrapper ref={unitRef}>
          <s.UnitChangeButton onClick={() => setOpenUnitChange(true)}>
            <span>기준</span>
            {formData.isCostYear ? "연" : "월"}
          </s.UnitChangeButton>
          {openUnitChange && (
            <s.UnitChangeDropdown>
              {unitList.map((i) => (
                <s.UnitChangeDropdownItem
                  key={i.label}
                  onClick={() =>
                    setFormData((prev) => ({ ...prev, isCostYear: i.value }))
                  }
                  $active={formData.isCostYear === i.value}
                >
                  {i.label}
                  {formData.isCostYear === i.value && (
                    <img src="/asset/images/check/Check24Green2.svg" alt="" />
                  )}
                </s.UnitChangeDropdownItem>
              ))}
            </s.UnitChangeDropdown>
          )}
        </s.UnitWrapper> */}
      </s.SectionTitleContainer>
      
      <s.SectionInner>
        <s.Label>
          <s.LabelTitle>경작기간 총 비용</s.LabelTitle>
          트랙터, 부속기를 제외한 대출이자, 인건비, 재료비 등 농작물 생산에 드는 대략적인 비용입니다.
        </s.Label>
        <s.FixedValue>{
          (
            formData.empLaborCost 
            + formData.ownLaborCost 
            + formData.materialCost 
            + formData.waterHeatingCost 
            + formData.farmEquipmentRentalCost
            + formData.sga
          ).toLocaleString()
          }원</s.FixedValue>
      </s.SectionInner>

      <s.SectionInner $top={8}>
        <s.Accordian onClick={() => setOpenDetailCost((prev) => !prev)}>
          상세 비용 입력
          <s.Arrow
            src="/asset/images/arrow/arrow_left_calendar_black.svg"
            alt=""
            $open={openDetailCost}
          />
        </s.Accordian>
      </s.SectionInner>
      {openDetailCost && (
        <>
          <s.SectionInner $top={8}>
            <s.Label>
              <s.LabelTitle>외주 인건비</s.LabelTitle>
            </s.Label>
            <s.InputWrapper>
              <s.Input
                value={empLaborDisplay}
                onChange={(e) => handleEmpLaborChange(e.target.value)}
                onBlur={handleEmpLaborBlur}
              />
              <span>원{unit === "총 비용" ? "" : ("/" + unit)}</span>
            </s.InputWrapper>
            <s.Checkbox
              onClick={() =>
                setFormData((prev) => ({
                  ...prev,
                  isOwnLabor: !prev.isOwnLabor,
                  ownLaborCost: prev.isOwnLabor ? 0 : prev.initialOwnLabor,
                }))
              }
            >
              <img
                src={`/asset/images/check/check-${formData.isOwnLabor ? "on" : "off"}.svg`}
                alt=""
              />
              자가 인건비
            </s.Checkbox>
          </s.SectionInner>
          {formData.isOwnLabor && (
            <s.SectionInner $top={8}>
              <s.Label>
                <s.LabelTitle>자가 인건비</s.LabelTitle>
              </s.Label>
              <s.InputWrapper>
                <s.Input
                  value={ownLaborDisplay}
                  onChange={(e) => handleOwnLaborChange(e.target.value)}
                  onBlur={handleOwnLaborBlur}
                />
                <span>원{unit === "총 비용" ? "" : ("/" + unit)}</span>
              </s.InputWrapper>
            </s.SectionInner>
          )}
          <s.SectionInner $top={8}>
            <s.Label>
              <s.LabelTitle>재료비(농약, 비료, 종자, 마대 등)</s.LabelTitle>
            </s.Label>
            <s.InputWrapper>
              <s.Input
                value={materialDisplay}
                onChange={(e) => handleMaterialChange(e.target.value)}
                onBlur={handleMaterialBlur}
              />
              <span>원{unit === "총 비용" ? "" : ("/" + unit)}</span>
            </s.InputWrapper>
          </s.SectionInner>
          <s.SectionInner $top={8}>
            <s.Label>
              <s.LabelTitle>수도광열비(유류비 포함)</s.LabelTitle>
            </s.Label>
            <s.InputWrapper>
              <s.Input
                value={waterHeatingDisplay}
                onChange={(e) => handleWaterHeatingChange(e.target.value)}
                onBlur={handleWaterHeatingBlur}
              />
              <span>원{unit === "총 비용" ? "" : ("/" + unit)}</span>
            </s.InputWrapper>
          </s.SectionInner>
          <s.SectionInner $top={8}>
            <s.Label>
              <s.LabelTitle>농구렌탈비(없으면 제외)</s.LabelTitle>
            </s.Label>
            <s.InputWrapper>
              <s.Input
                value={rentalDisplay}
                onChange={(e) => handleRentalChange(e.target.value)}
                onBlur={handleRentalBlur}
              />
              <span>원{unit === "총 비용" ? "" : ("/" + unit)}</span>
            </s.InputWrapper>
          </s.SectionInner>
          <s.SectionInner $top={8}>
            <s.Label>
              <s.LabelTitle>판매관리비 합계</s.LabelTitle>
            </s.Label>            
            <s.InputWrapper>
              <s.Input
                value={totalSga.toLocaleString()}
                onChange={(e) => setTotalSga(formatNumber(e.target.value))}
                // onBlur={handleSgaBlur}
              />
              <span>원{unit === "총 비용" ? "" : ("/" + unit)}</span>
            </s.InputWrapper>
            <s.Checkbox
              onClick={() => setOpenSgaCost((prev) => !prev)}
            >
              <img
                src={`/asset/images/check/check-${openSgaCost ? "on" : "off"}.svg`}
                alt=""
              />
              판매관리비 상세 입력
            </s.Checkbox>            
          </s.SectionInner>
          {openSgaCost && (
            <>
              <s.SectionInner $top={8}>
                <s.Label>
                  <s.LabelTitle>농기계 수리비(트랙터/부속기 제외)</s.LabelTitle>
                </s.Label>
                <s.InputWrapper>
                  <s.Input
                    value={repairCost.toLocaleString()}
                    onChange={(e) => setRepairCost(formatNumber(e.target.value))}
                    // onBlur={handleFinancialBlur}
                  />
                  <span>원{unit === "총 비용" ? "" : ("/" + unit)}</span>
                </s.InputWrapper>
              </s.SectionInner>
              <s.SectionInner $top={8}>
                <s.Label>
                  <s.LabelTitle>농지 임대료</s.LabelTitle>
                </s.Label>
                <s.InputWrapper>
                  <s.Input
                    value={landRentalCost.toLocaleString()}
                    onChange={(e) => setLandRentalCost(formatNumber(e.target.value))}
                    // onBlur={handleFinancialBlur}
                  />
                  <span>원{unit === "총 비용" ? "" : ("/" + unit)}</span>
                </s.InputWrapper>
              </s.SectionInner>
              <s.SectionInner $top={8}>
                <s.Label>
                  <s.LabelTitle>금융비(대출 이자 등)</s.LabelTitle>
                </s.Label>
                <s.InputWrapper>
                  <s.Input
                    value={financeCost.toLocaleString()}
                    onChange={(e) => setFinanceCost(formatNumber(e.target.value))}
                    // onBlur={handleFinancialBlur}
                  />
                  <span>원{unit === "총 비용" ? "" : ("/" + unit)}</span>
                </s.InputWrapper>
              </s.SectionInner>
              <s.SectionInner $top={8}>
                <s.Label>
                  <s.LabelTitle>운송비</s.LabelTitle>
                </s.Label>
                <s.InputWrapper>
                  <s.Input
                    value={transportationCost.toLocaleString()}
                    onChange={(e) => setTransportationCost(formatNumber(e.target.value))}
                    // onBlur={handleFinancialBlur}
                  />
                  <span>원{unit === "총 비용" ? "" : ("/" + unit)}</span>
                </s.InputWrapper>
              </s.SectionInner>
              <s.SectionInner $top={8}>
                <s.Label>
                  <s.LabelTitle>식비</s.LabelTitle>
                </s.Label>
                <s.InputWrapper>
                  <s.Input
                    value={mealCost.toLocaleString()}
                    onChange={(e) => setMealCost(formatNumber(e.target.value))}
                    // onBlur={handleFinancialBlur}
                  />
                  <span>원{unit === "총 비용" ? "" : ("/" + unit)}</span>
                </s.InputWrapper>
              </s.SectionInner>
              <s.SectionInner $top={8}>
                <s.Label>
                  <s.LabelTitle>사무용품 및 잡비</s.LabelTitle>
                </s.Label>
                <s.InputWrapper>
                  <s.Input
                    value={officeSuppliesCost.toLocaleString()}
                    onChange={(e) => setOfficeSuppliesCost(formatNumber(e.target.value))}
                    // onBlur={handleFinancialBlur}
                  />
                  <span>원{unit === "총 비용" ? "" : ("/" + unit)}</span>
                </s.InputWrapper>
              </s.SectionInner>
              <s.SectionInner $top={8}>
                <s.Label>
                  <s.LabelTitle>기타</s.LabelTitle>
                </s.Label>
                <s.InputWrapper>
                  <s.Input
                    value={othersCost.toLocaleString()}
                    onChange={(e) => setOthersCost(formatNumber(e.target.value))}
                    // onBlur={handleFinancialBlur}
                  />
                  <span>원{unit === "총 비용" ? "" : ("/" + unit)}</span>
                </s.InputWrapper>
              </s.SectionInner>
            </>
          )}
          {/* <s.SectionInner $top={16}>
            <s.Label>
              <s.LabelTitle>연 금융비</s.LabelTitle>
            </s.Label>
            <s.Stepper>
              <s.StepTextButton
                $bg="#F1F1FE"
                $color="#4B47FC"
                onClick={() => updateCostByRate("financialCost", 0.9)}
              >
                -10%
              </s.StepTextButton>
              <s.StepperInner>
                <s.StepperInput
                  value={financialDisplay}
                  onChange={(e) => handleFinancialChange(e.target.value)}
                  onBlur={handleFinancialBlur}
                />
                <s.StepperUnit>만원</s.StepperUnit>
              </s.StepperInner>
              <s.StepTextButton
                $bg="#FFF6F6"
                $color="#EC4F4F"
                onClick={() => updateCostByRate("financialCost", 1.1)}
              >
                +10%
              </s.StepTextButton>
            </s.Stepper>
          </s.SectionInner>
          <s.SectionInner $top={8}>
            <s.Label>
              <s.LabelTitle>연 인건비</s.LabelTitle>
            </s.Label>
            <s.Stepper>
              <s.StepTextButton
                $bg="#F1F1FE"
                $color="#4B47FC"
                onClick={() => updateCostByRate("empLaborCost", 0.9)}
              >
                -10%
              </s.StepTextButton>
              <s.StepperInner>
                <s.StepperInput
                  value={empLaborDisplay}
                  onChange={(e) => handleEmpLaborChange(e.target.value)}
                  onBlur={handleEmpLaborBlur}
                />
                <s.StepperUnit>만원</s.StepperUnit>
              </s.StepperInner>
              <s.StepTextButton
                $bg="#FFF6F6"
                $color="#EC4F4F"
                onClick={() => updateCostByRate("empLaborCost", 1.1)}
              >
                +10%
              </s.StepTextButton>
            </s.Stepper>
            <s.Checkbox
              onClick={() =>
                setFormData((prev) => ({
                  ...prev,
                  isOwnLabor: !prev.isOwnLabor,
                  ownLaborCost: prev.isOwnLabor ? 0 : prev.initialOwnLabor,
                }))
              }
            >
              <img
                src={`/asset/images/check/check-${formData.isOwnLabor ? "on" : "off"}.svg`}
                alt=""
              />
              자가 인건비
            </s.Checkbox>
          </s.SectionInner>
          {formData.isOwnLabor && (
            <s.SectionInner $top={8}>
              <s.Label>
                <s.LabelTitle>연 자가 인건비</s.LabelTitle>
              </s.Label>
              <s.Stepper>
                <s.StepTextButton
                  $bg="#F1F1FE"
                  $color="#4B47FC"
                  onClick={() => updateCostByRate("ownLaborCost", 0.9)}
                >
                  -10%
                </s.StepTextButton>
                <s.StepperInner>
                  <s.StepperInput
                    value={ownLaborDisplay}
                    onChange={(e) => handleOwnLaborChange(e.target.value)}
                    onBlur={handleOwnLaborBlur}
                  />
                  <s.StepperUnit>만원</s.StepperUnit>
                </s.StepperInner>
                <s.StepTextButton
                  $bg="#FFF6F6"
                  $color="#EC4F4F"
                  onClick={() => updateCostByRate("ownLaborCost", 1.1)}
                >
                  +10%
                </s.StepTextButton>
              </s.Stepper>
            </s.SectionInner>
          )}
          <s.SectionInner $top={8}>
            <s.Label>
              <s.LabelTitle>연 재료비</s.LabelTitle>
            </s.Label>
            <s.Stepper>
              <s.StepTextButton
                $bg="#F1F1FE"
                $color="#4B47FC"
                onClick={() => updateCostByRate("materialCost", 0.9)}
              >
                -10%
              </s.StepTextButton>
              <s.StepperInner>
                <s.StepperInput
                  value={materialDisplay}
                  onChange={(e) => handleMaterialChange(e.target.value)}
                  onBlur={handleMaterialBlur}
                />
                <s.StepperUnit>만원</s.StepperUnit>
              </s.StepperInner>
              <s.StepTextButton
                $bg="#FFF6F6"
                $color="#EC4F4F"
                onClick={() => updateCostByRate("materialCost", 1.1)}
              >
                +10%
              </s.StepTextButton>
            </s.Stepper>
          </s.SectionInner>
          <s.SectionInner $top={8}>
            <s.Label>
              <s.LabelTitle>수도광열비</s.LabelTitle>
            </s.Label>
            <s.Stepper>
              <s.StepTextButton
                $bg="#F1F1FE"
                $color="#4B47FC"
                onClick={() => updateCostByRate("waterHeatingCost", 0.9)}
              >
                -10%
              </s.StepTextButton>
              <s.StepperInner>
                <s.StepperInput
                  value={waterHeatingDisplay}
                  onChange={(e) => handleWaterHeatingChange(e.target.value)}
                  onBlur={handleWaterHeatingBlur}
                />
                <s.StepperUnit>만원</s.StepperUnit>
              </s.StepperInner>
              <s.StepTextButton
                $bg="#FFF6F6"
                $color="#EC4F4F"
                onClick={() => updateCostByRate("waterHeatingCost", 1.1)}
              >
                +10%
              </s.StepTextButton>
            </s.Stepper>
          </s.SectionInner>
          <s.SectionInner $top={8}>
            <s.Label>
              <s.LabelTitle>농구비</s.LabelTitle>
            </s.Label>
            <s.Stepper>
              <s.StepTextButton
                $bg="#F1F1FE"
                $color="#4B47FC"
                onClick={() => updateCostByRate("farmEquipmentRentalCost", 0.9)}
              >
                -10%
              </s.StepTextButton>
              <s.StepperInner>
                <s.StepperInput
                  value={rentalDisplay}
                  onChange={(e) => handleRentalChange(e.target.value)}
                  onBlur={handleRentalBlur}
                />
                <s.StepperUnit>만원</s.StepperUnit>
              </s.StepperInner>
              <s.StepTextButton
                $bg="#FFF6F6"
                $color="#EC4F4F"
                onClick={() => updateCostByRate("farmEquipmentRentalCost", 1.1)}
              >
                +10%
              </s.StepTextButton>
            </s.Stepper>
          </s.SectionInner>
          <s.SectionInner $top={8}>
            <s.Label>
              <s.LabelTitle>기타 판매관리비</s.LabelTitle>
            </s.Label>
            <s.Stepper>
              <s.StepTextButton
                $bg="#F1F1FE"
                $color="#4B47FC"
                onClick={() => updateCostByRate("sga", 0.9)}
              >
                -10%
              </s.StepTextButton>
              <s.StepperInner>
                <s.StepperInput
                  value={sgaDisplay}
                  onChange={(e) => handleSgaChange(e.target.value)}
                  onBlur={handleSgaBlur}
                />
                <s.StepperUnit>만원</s.StepperUnit>
              </s.StepperInner>
              <s.StepTextButton
                $bg="#FFF6F6"
                $color="#EC4F4F"
                onClick={() => updateCostByRate("sga", 1.1)}
              >
                +10%
              </s.StepTextButton>
            </s.Stepper>
          </s.SectionInner> */}
          {/* <s.SectionInner>
            <s.Label>
              <s.LabelTitle>합계</s.LabelTitle>
              매년 지출하는 총 비용입니다.
            </s.Label>
            <s.FixedValue>{formatManToEokMan(totalCost)}</s.FixedValue>
            {totalCost > formData.sales && (
              <s.InputWarningMessage style={{ padding: 0 }}>
                비용이 매출보다 높습니다.
              </s.InputWarningMessage>
            )}
          </s.SectionInner> */}
        </>
      )}
    </s.Section>
  );
}

export default CostSection;
